import React, { useEffect, useRef, useState } from "react";
import { Policy } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { caseActions } from "@_actions/case.actions";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import { casesActions } from "@_actions/cases.actions";
import { caseConstants } from "@_constants/case.constants";
import { LicenseConstants } from "@_constants/license.constants";

import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";
import LicensePopup from "@_components/popups/LicensePopup/LicensePopup";

import { useConfirmPopup } from "@_hooks/useConfirmPopup";
import { useSnackbar } from "@_hooks/useSnackbar";
import { catchError } from "@_utils/helpers";

import CardShell from "../core/CardShell";
import styles from "../CaseActivityCard.module.css";

// ── Helpers ───────────────────────────────────────────────────────────────────

function LicenseFileLink({ url, filename, deleted }) {
  if (deleted) {
    return (
      <span className={styles.license_filename_with_status} data-testid="license-filename">
        <span className={styles.license_filename_text}>{filename}</span>
        <span className={styles.license_deleted}> (deleted)</span>
      </span>
    );
  }
  if (url) {
    return (
      <a
        data-testid="license-filename"
        className={styles.license_file_link}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {filename}
      </a>
    );
  }
  return <span data-testid="license-filename">{filename}</span>;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * LicenseActivityCard
 *
 * Handles:
 *   CASEACTIVITY_TYPE_LICENSE_UPLOADED  – shows filename, "CHECK LICENSE" button, delete action
 *   CASEACTIVITY_TYPE_LICENSE_ACCEPTED  – shows filename (read-only)
 *   CASEACTIVITY_TYPE_LICENSE_REJECTED  – shows filename (read-only)
 */
export default function LicenseActivityCard({
  activity,
  caseData,
  resumeAutomation,
}) {
  const dispatch = useDispatch();
  const caseReducer = useSelector((s) => s.caseReducer);
  const confirm = useConfirmPopup();
  const { snackbarProps, showSnackbarSuccess, showSnackbarError } =
    useSnackbar();
  const [openCheckLicensePopup, setOpenCheckLicensePopup] = useState(false);
  // Tracks whether THIS card initiated the delete, so only it reacts to the
  // global CASE_DELETE_LICENSE status change (prevents N cards from each
  // dispatching the refresh actions and creating a network storm).
  const deleteInitiatedRef = useRef(false);

  const isUploaded =
    activity.type === caseConstants.CASEACTIVITY_TYPE_LICENSE_UPLOADED;

  const licenseDeleted = isUploaded && activity.content.state === "DELETED";
  const licenseUnchecked =
    isUploaded && activity.content.state === LicenseConstants.STATE_UNCHECKED;

  const canDelete =
    isUploaded &&
    !licenseDeleted &&
    (caseData?.state === caseConstants.CASE_STATE_ACTIVE ||
      caseData?.state === caseConstants.CASE_STATE_CANCELLED);

  const licenseFinding = isUploaded
    ? caseData?.findings?.find(
        (f) => f.license && f.license.id === activity.content.license_id,
      )
    : null;

  // ── Effect: sync Redux after delete ────────────────────────────────────────
  /**
   * After a license is deleted from a license activity row (`CASE_DELETE_LICENSE` success),
   * sync Redux case, list, and activities.
   * Clear delete status
   * Refresh cases list
   * Refetch current case
   * Refresh activity timeline
   * issue: No Error Handling
   */
  useEffect(() => {
    if (!isUploaded) return;
    // Only the card that initiated the delete should react to the global
    // CASE_DELETE_LICENSE status change; otherwise every LicenseActivityCard
    // on the page would dispatch the refresh actions in parallel.
    if (!deleteInitiatedRef.current) return;

    const status = caseReducer.status["CASE_DELETE_LICENSE"];

    if (status === "success") {
      deleteInitiatedRef.current = false;
      dispatch(caseActions.clearStateMessage("CASE_DELETE_LICENSE"));
      showSnackbarSuccess("License deleted successfully.");
      dispatch(casesActions.getCases());
      dispatch(
        caseActions.getCaseWithChargeDetails(
          activity.case_id,
          caseData?.overview,
        ),
      );
      dispatch(caseActivityActions.getCaseActivities(activity.case_id));
    } else if (catchError(status)) {
      deleteInitiatedRef.current = false;
      dispatch(caseActions.clearStateMessage("CASE_DELETE_LICENSE"));
      showSnackbarError("Failed to delete license. Please try again.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseReducer.status["CASE_DELETE_LICENSE"]]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    confirm.prompt("Are you sure you want to delete this license?", () => {
      confirm.close();
      if (licenseFinding) {
        deleteInitiatedRef.current = true;
        dispatch(
          caseActions.deleteCaseLicense(
            activity.case_id,
            caseData.findings.indexOf(licenseFinding),
            licenseFinding.id,
          ),
        );
      }
    });
  };

  // ── Type labels ────────────────────────────────────────────────────────────

  const typeTextMap = {
    [caseConstants.CASEACTIVITY_TYPE_LICENSE_UPLOADED]: "License uploaded",
    [caseConstants.CASEACTIVITY_TYPE_LICENSE_ACCEPTED]: "License accepted",
    [caseConstants.CASEACTIVITY_TYPE_LICENSE_REJECTED]: "License rejected",
  };

  // ── Content preview ────────────────────────────────────────────────────────

  const contentPreview = (() => {
    if (isUploaded) {
      return (
        <div className={styles.license_content}>
          <div
            className={styles.license_filename}
            title={activity.content.filename}
          >
            <LicenseFileLink
              url={activity.content.url}
              filename={activity.content.filename}
              deleted={licenseDeleted}
            />
          </div>
          {licenseUnchecked && (
            <Button
              data-testid="check-license-button"
              className={styles.btn_check_license}
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setOpenCheckLicensePopup(true);
              }}
            >
              CHECK LICENSE
            </Button>
          )}
        </div>
      );
    }

    // ACCEPTED / REJECTED
    const filename = activity.content.license_filename;
    const url = activity.content.license_url;
    return (
      <div className={styles.license_content}>
        <div className={styles.license_filename} title={filename}>
          <LicenseFileLink url={url} filename={filename} deleted={false} />
        </div>
      </div>
    );
  })();

  const openerColumn = canDelete ? (
    <DeleteIcon
      data-testid="license-delete-icon"
      className={styles.delete_shopping_icon}
      onClick={handleDeleteClick}
    />
  ) : null;

  return (
    <>
      <CardShell
        activity={activity}
        icon={
          <Policy
            className={
              activity.priority === "HIGH"
                ? styles.license_icon_high
                : undefined
            }
          />
        }
        typeText={<span data-testid="license-type-text">{typeTextMap[activity.type]}</span>}
        typeSubText={activity.user_name || "Website owner"}
        contentPreview={contentPreview}
        openerColumn={openerColumn}
      />

      {isUploaded && openCheckLicensePopup && (
        <LicensePopup
          open={openCheckLicensePopup}
          close={() => setOpenCheckLicensePopup(false)}
          data={caseData}
          caseId={activity.case_id}
          filterLicenseId={activity.content.license_id}
          resumeAutomation={resumeAutomation}
        />
      )}

      <AreYouSurePopup
        open={confirm.open}
        close={confirm.close}
        onClick={confirm.action}
      >
        <p data-testid="license-delete-confirmation-text">{confirm.text}</p>
      </AreYouSurePopup>

      <AppSnackbar {...snackbarProps} />
    </>
  );
}
